/**
 * Manavta AI Engine
 * Demo-ready AI features powered by simulated responses.
 */

export interface AIMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export interface AIImpactReport {
  headline: string;
  summary: string;
  achievements: string[];
  nextGoal: string;
  tip: string;
  badge: string;
}

export interface AICauseMatch {
  causeId: string;
  title: string;
  institution: string;
  matchScore: number;
  reason: string;
  suggestedAmount: number;
}

export interface AIFraudScore {
  score: number;
  riskLevel: "low" | "medium" | "high";
  signals: string[];
  recommendation: string;
}

export interface AITaxSummary {
  totalDeductible: number;
  taxSaved: number;
  slab: string;
  advice: string;
}

export interface AIDonationNudge {
  message: string;
  cta: string;
  urgency: "low" | "medium" | "high";
  causeId?: string;
}

export interface AISearchResult {
  type: "cause" | "institution" | "initiative" | "faq";
  title: string;
  subtitle: string;
  href: string;
  score: number;
}

export interface AIAdminInsight {
  healthScore: number;
  riskLevel: "low" | "medium" | "high";
  alerts: string[];
  recommendations: { title: string; action: string; impact: string }[];
  complianceScore: number;
  payoutAdvice: string;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── CHATBOT (PUBLIC) ─────────────────────────────────────────────────────────

const KB: Record<string, string> = {
  "80g":
    "All donations through Manavta are eligible for 80G tax deduction. Your receipt is generated instantly after payment.",
  tax: "Donations qualify under Section 80G. In the 30% slab, donating Rs.10,000 saves you Rs.3,000 in taxes. Use the AI Tax Advisor in your Donor Console.",
  receipt:
    "Your 80G receipt is emailed instantly. Download it anytime from Donor Console > Donation History.",
  refund: "We have a 24-hour no-questions-asked refund policy. Contact support@manavta.org.",
  verify:
    "Every institution is manually verified — registration docs, 12A certificates, PAN, and phone verification.",
  inkind:
    "In-Kind donations let you sponsor physical items — green fodder, bird grain, medical kits — dispatched by verified vendors.",
  monthly:
    "Set up monthly recurring donations from your Donor Console. Cancel anytime with one click.",
  gaushala:
    "Gaushalas care for abandoned and injured cattle. The oldest on our platform — Shree Ahmedabad Panjrapole — was founded in 1817!",
  animal:
    "Animal Seva covers panjrapoles, bird hospitals, stray dog rescue, and wildlife rehabilitation across Gujarat.",
  volunteer:
    "Register as a volunteer from the Volunteer page. Opportunities include rescue ops, data entry, and awareness drives.",
  donate:
    "You can donate cash (instant 80G receipt) or sponsor In-Kind items like fodder and grain. Shall I suggest a cause?",
  how: "Manavta is a transparent donation platform for animal welfare in India. 100% of your donation reaches the cause — zero platform fees.",
  trust:
    "Manavta is registered under Section 80G and 12A. All partner institutions are manually verified before listing.",
};

function matchKB(query: string): string | null {
  const q = query.toLowerCase();
  for (const [key, answer] of Object.entries(KB)) {
    if (q.includes(key)) return answer;
  }
  return null;
}

const FALLBACKS = [
  "That's a great question! Manavta ensures every rupee is tracked and reaches the right cause. Want me to help you find a cause to support?",
  "I'm here to help you give with confidence! You can explore verified causes, check 80G eligibility, or set up monthly giving. What would you like to do?",
  "Our team manually verifies every partner institution before listing. Would you like to know more about our verification process?",
  "Manavta is registered under 80G and 12A — all donations are tax-deductible. Shall I show you how much you could save?",
  "You can donate cash or sponsor in-kind items like fodder and medical kits. How can I guide you?",
  "Try exploring our Causes page to find the most urgent animal welfare needs. I can help you pick based on your interests!",
];

let fallbackIdx = 0;

export async function chatWithAI(messages: AIMessage[]): Promise<string> {
  await delay(900 + Math.random() * 600);
  const last = messages[messages.length - 1];
  if (!last || last.role !== "user")
    return "Namaste! How can I help you support animal welfare today?";

  const kbAnswer = matchKB(last.content);
  if (kbAnswer) return kbAnswer;

  if (messages.length > 2) {
    const prev = messages[messages.length - 3]?.content?.toLowerCase() ?? "";
    if (prev.includes("donate") || prev.includes("give")) {
      return "Great! You can choose between Cash (instant 80G receipt) or In-Kind material seva. Which would you prefer?";
    }
  }

  const reply = FALLBACKS[fallbackIdx % FALLBACKS.length];
  fallbackIdx++;
  return reply;
}

// ─── ADMIN AI COPILOT ────────────────────────────────────────────────────────

const ADMIN_KB: Record<string, string> = {
  payout:
    "AI Payout Audit: All 3 pending settlement requests have passed bank verification. Shree Ahmedabad Panjrapole is due Rs.2,45,000 for fodder procurement.",
  fraud:
    "AI Fraud Audit: 1 application requires verification due to missing 12A registration certificate. Low overall risk index (94/100).",
  audit:
    "System Compliance: 100% of 80G receipts generated today match Form 10BE requirements. Platform zero-fee audit verified.",
  donor:
    "Donor Intelligence: Repeat donor retention is up 14% this month. Top donor segment (Diamond Sevaks) contributed 48% of total volume.",
  gaushala:
    "Gaushala Monitoring: Naroda Panjrapole fodder stock is at 18 days. Recommended payout release of Rs.1,20,000.",
  urgent:
    "Urgent Alert: Dharampur Gaushala Trust in Banaskantha has a critical water scarcity report — fast-track payout recommended.",
};

export async function chatWithAdminAI(messages: AIMessage[]): Promise<string> {
  await delay(800 + Math.random() * 500);
  const last = messages[messages.length - 1];
  if (!last || last.role !== "user")
    return "Super Admin AI Copilot active. How can I assist with platform oversight today?";

  const q = last.content.toLowerCase();
  for (const [k, v] of Object.entries(ADMIN_KB)) {
    if (q.includes(k)) return v;
  }

  return `AI Admin Analysis: Reviewed ${last.content}. Platform metrics show 99.8% uptime, zero 80G discrepancy, and 12 verified gaushalas active. All settlement pipelines operational.`;
}

// ─── ADMIN EXECUTIVE SUMMARY ─────────────────────────────────────────────────

export async function generateAdminExecutiveSummary(): Promise<AIAdminInsight> {
  await delay(1000);
  return {
    healthScore: 96,
    riskLevel: "low",
    alerts: [
      "Banaskantha shelter water supply below 15 days threshold — priority payout flagged",
      "1 partner onboarding application pending document validation (12A certificate)",
      "Razorpay instant settlement gateway performing at 100% accuracy",
    ],
    recommendations: [
      {
        title: "Disburse Green Fodder Grant",
        action: "Approve Rs.1,50,000 payout to Naroda Panjrapole",
        impact: "Sustains 5,400 cows for 21 days",
      },
      {
        title: "Verify Avian ICU Application",
        action: "Request 12A renewal copy from applicant",
        impact: "Enables fast-track onboarding of bird shelter",
      },
      {
        title: "Trigger 80G Batch Filing",
        action: "Generate Form 10BE export for Q2 donors",
        impact: "Ensures 100% tax compliance for 340 donors",
      },
    ],
    complianceScore: 99,
    payoutAdvice:
      "All bank account penny-drop validations passed. Recommended disburse total: Rs.4,85,000.",
  };
}

// ─── IMPACT REPORT ───────────────────────────────────────────────────────────

export async function generateImpactReport(
  totalDonated: number,
  donationCount: number,
  institutionCount: number,
): Promise<AIImpactReport> {
  await delay(1200);
  const cows = Math.round((totalDonated * 0.42) / 60);
  const birds = Math.round((totalDonated * 0.18) / 150);
  const dogs = Math.round((totalDonated * 0.28) / 1200);
  return {
    headline: `Your seva has touched ${cows + birds + dogs}+ animals this year`,
    summary: `Through ${donationCount} donations across ${institutionCount} partner institutions, your generosity has created measurable impact on the ground.`,
    achievements: [
      `Fed ${cows} cows for a full month at Naroda Panjrapole`,
      `Funded care for ${birds} injured birds at Jivdaya ICU`,
      `Supported vaccination for ${dogs} stray dogs in Gandhinagar`,
      `Generated ${donationCount} 80G receipts saving ~Rs.${Math.round(totalDonated * 0.3).toLocaleString("en-IN")} in taxes`,
      `You are in the top 8% of Manavta donors — Diamond Sevak tier`,
    ],
    nextGoal: `Reach Rs.${(Math.ceil(totalDonated / 25000) * 25000).toLocaleString("en-IN")} lifetime giving to unlock Platinum Sevak`,
    tip: `AI Tip: Switch to monthly giving of Rs.2,001/month and you'll hit your yearly goal by October while maximising your 80G deduction.`,
    badge: "Diamond Sevak",
  };
}

// ─── CAUSE MATCHER ───────────────────────────────────────────────────────────

export async function matchCauses(
  preferences: string[],
  _pastCauses: string[],
): Promise<AICauseMatch[]> {
  await delay(800);
  const allMatches: AICauseMatch[] = [
    {
      causeId: "green-fodder-naroda-2026",
      title: "Green Fodder Truckload — Naroda Panjrapole",
      institution: "Shree Ahmedabad Panjrapole",
      matchScore: 97,
      reason: "Matches your Animal Seva history and monthly giving pattern",
      suggestedAmount: 5100,
    },
    {
      causeId: "avian-icu-jivdaya",
      title: "Avian ICU Emergency Surgery Fund",
      institution: "Jivdaya Charitable Trust",
      matchScore: 91,
      reason: "You've previously supported bird welfare — this is their most urgent need",
      suggestedAmount: 2500,
    },
    {
      causeId: "stray-dog-vaccination",
      title: "Stray Dog Anti-Rabies Vaccination Drive",
      institution: "People for Animals, Gandhinagar",
      matchScore: 84,
      reason: "Trending in your city — 3 donors from Ahmedabad supported this week",
      suggestedAmount: 1200,
    },
    {
      causeId: "water-tanker-banaskantha",
      title: "Summer Water Tanker for Remote Gaushala",
      institution: "Dharampur Gaushala Trust",
      matchScore: 79,
      reason: "Critical urgency — only 12 days of water supply remaining",
      suggestedAmount: 2500,
    },
  ];
  if (preferences.includes("monthly")) {
    allMatches[0].matchScore = Math.min(100, allMatches[0].matchScore + 5);
  }
  return allMatches.sort((a, b) => b.matchScore - a.matchScore);
}

// ─── FRAUD DETECTOR ──────────────────────────────────────────────────────────

export async function analyzeApplicationFraud(app: {
  org_name: string;
  reg_80g?: string;
  reg_12a?: string;
  registration_no: string;
  animals_cared: number;
  about?: string;
  city: string;
}): Promise<AIFraudScore> {
  await delay(1400);
  let score = 85;
  const signals: string[] = [];
  if (!app.reg_80g) {
    score -= 20;
    signals.push("Missing 80G certificate number");
  }
  if (!app.reg_12a) {
    score -= 15;
    signals.push("Missing 12A certificate number");
  }
  if (app.animals_cared > 5000) {
    score -= 8;
    signals.push("Unusually high animal count — verify on-site");
  }
  if ((app.about?.length ?? 0) < 80) {
    score -= 10;
    signals.push("Organisation description too brief");
  }
  if (!app.registration_no.match(/\d{4,}/)) {
    score -= 12;
    signals.push("Registration number format irregular");
  }
  if (app.reg_80g && app.reg_12a) signals.push("Both 80G and 12A certificates provided");
  if (app.animals_cared >= 100 && app.animals_cared <= 2000)
    signals.push("Animal count in expected range");
  if (app.city) signals.push(`Location verified — ${app.city}`);
  score = Math.max(20, Math.min(100, score));
  return {
    score,
    riskLevel: score >= 75 ? "low" : score >= 50 ? "medium" : "high",
    signals,
    recommendation:
      score >= 75
        ? "Application looks genuine. Recommend approval after standard phone verification."
        : score >= 50
          ? "Application needs additional document verification before approval."
          : "High risk signals detected. Escalate to senior admin for manual review.",
  };
}

// ─── TAX ADVISOR ─────────────────────────────────────────────────────────────

export async function generateTaxSummary(totalDonated: number): Promise<AITaxSummary> {
  await delay(700);
  const slab = totalDonated > 50000 ? "30%" : totalDonated > 20000 ? "20%" : "5%";
  const rate = totalDonated > 50000 ? 0.3 : totalDonated > 20000 ? 0.2 : 0.05;
  const taxSaved = Math.round(totalDonated * rate);
  return {
    totalDeductible: totalDonated,
    taxSaved,
    slab,
    advice: `Based on Rs.${totalDonated.toLocaleString("en-IN")} in donations, you can claim Rs.${taxSaved.toLocaleString("en-IN")} in tax savings under Section 80G. File Form 10BE through your CA to maximise. Our AI estimates you'd save an additional Rs.${Math.round(taxSaved * 0.3).toLocaleString("en-IN")} by increasing your annual giving by Rs.10,000.`,
  };
}

// ─── DONATION NUDGE ──────────────────────────────────────────────────────────

export async function getDonationNudge(
  daysSinceLastDonation: number,
  totalDonated: number,
): Promise<AIDonationNudge> {
  await delay(500);
  if (daysSinceLastDonation > 30) {
    return {
      message: `It's been ${daysSinceLastDonation} days since your last donation. The animals need you!`,
      cta: "Give today",
      urgency: "high",
      causeId: "green-fodder-naroda-2026",
    };
  }
  if (daysSinceLastDonation > 14) {
    return {
      message: "A critical bird surgery campaign needs funding this week.",
      cta: "See urgent causes",
      urgency: "medium",
    };
  }
  return {
    message: `You're Rs.${(50000 - totalDonated).toLocaleString("en-IN")} away from Diamond Sevak tier!`,
    cta: "Level up",
    urgency: "low",
  };
}

// ─── SMART SEARCH ─────────────────────────────────────────────────────────────

export async function smartSearch(query: string): Promise<AISearchResult[]> {
  await delay(300);
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const corpus: AISearchResult[] = [
    {
      type: "institution",
      title: "Shree Ahmedabad Panjrapole",
      subtitle: "Panjrapol · Ahmedabad · 5,400 animals",
      href: "/institutions/shree-ahmedabad-panjrapole",
      score: 0,
    },
    {
      type: "institution",
      title: "Jivdaya Charitable Trust",
      subtitle: "Bird hospital · Ahmedabad · Avian ICU",
      href: "/institutions/jivdaya-charitable-trust",
      score: 0,
    },
    {
      type: "institution",
      title: "People for Animals, Gandhinagar",
      subtitle: "Stray dog rescue · Gandhinagar",
      href: "/institutions/people-for-animals-gandhinagar",
      score: 0,
    },
    {
      type: "initiative",
      title: "Animal Seva — Panjrapoles & Gaushalas",
      subtitle: "Donate to verified animal shelters",
      href: "/initiatives/animal-seva",
      score: 0,
    },
    {
      type: "cause",
      title: "Green Fodder Truckload — Naroda",
      subtitle: "Rs.5,100 · Urgent · 84% funded",
      href: "/causes",
      score: 0,
    },
    {
      type: "cause",
      title: "Avian ICU Emergency Surgery Fund",
      subtitle: "Rs.2,500 · Critical · Bird care",
      href: "/causes",
      score: 0,
    },
    {
      type: "faq",
      title: "How does 80G tax deduction work?",
      subtitle: "All donations are Section 80G eligible",
      href: "/faq",
      score: 0,
    },
    {
      type: "faq",
      title: "How do I get my donation receipt?",
      subtitle: "Instant via email and SMS",
      href: "/faq",
      score: 0,
    },
    {
      type: "faq",
      title: "Can I donate in-kind (fodder, grain)?",
      subtitle: "Yes — vendor-dispatched to gaushala",
      href: "/donate",
      score: 0,
    },
  ];

  const results: AISearchResult[] = [];
  for (const item of corpus) {
    let score = 0;
    const searchText = (item.title + " " + item.subtitle).toLowerCase();
    if (searchText.includes(q)) score += 80;
    const words = q.split(" ").filter(Boolean);
    for (const w of words) {
      if (w.length >= 3 && searchText.includes(w)) score += 20;
    }
    if (score > 0) results.push({ ...item, score });
  }

  return results.sort((a, b) => b.score - a.score).slice(0, 5);
}
